import React, { useState, useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Mail, Bell, ChevronDown, User, LogOut } from "lucide-react";
import Notifications from "./Notifications";
import { deleteApiKey } from "../../App";
import NewMessageDialog from "./components/NewMessageDialog";
import NotificationButton from "./NotificationButton";
import ManualButton from "./ManualButton";
import Search from "../content/components/Search";
import SearchStudents from "./SearchStudents";
import ClickAwayListener from "./components/ClickAwayListener";
import Menu from "./components/Menu";
import MenuItem from "./components/MenuItem";
import Avatar from "./components/Avatar";
import { UserContext, LanguageContext } from "../../Context";
import "./topbar-premium.css";

/**
 * Topbar navigation component with search, notifications, and user menu.
 * Premium flat design with lucide-react icons. No MUI components.
 * Hardcoded teal theme (#0f766e / #115e59).
 *
 * @param {object} props Component props
 * @param {object} props.user Current user
 * @param {function} props.setUser Function to set user
 * @param {number} props.language Current language (0=EN, 1=VN)
 * @param {function} props.setLanguage Function to set language
 * @author Forge
 * @date 2026-04-07
 * @version 6.0
 */
export default function Topbar() {
  const { user, setUser } = useContext(UserContext);
  const { language, setLanguage } = useContext(LanguageContext);

  const [anchorEl, setAnchorEl] = useState(null);
  const [NanchorEl, setNAnchorEl] = useState(null);
  const [search, setSearch] = useState("");
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [messageDialogOpen, setMessageDialogOpen] = useState(false);

  const isMenuOpen = Boolean(anchorEl);
  const isNMenuOpen = Boolean(NanchorEl);
  const isSearchOpen = isSearchFocused && search !== "";
  const navigate = useNavigate();

  const menuId = "primary-search-account-menu";

  const changeLanguage = () => {
    setLanguage(language === 0 ? 1 : 0);
  };

  const showNotifications = (event) => {
    setNAnchorEl(event.currentTarget);
  };

  const handleNotificationsClose = () => {
    setNAnchorEl(null);
  };

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    navigate("/profile");
  };

  const handleLogout = () => {
    setAnchorEl(null);
    localStorage.setItem("apiKey", undefined);
    deleteApiKey();
    setUser(undefined);
  };

  const closeSearchDropdown = () => {
    setIsSearchFocused(false);
  };

  return (
    <div className="topbar">
      <nav className="topbar-nav">
        {/* Search Section */}
        <div className="topbar-search-section">
          <ClickAwayListener onClickAway={closeSearchDropdown}>
            <div className="topbar-search-wrapper" onFocus={() => setIsSearchFocused(true)}>
              <Search
                search={search}
                setSearch={setSearch}
                placeholder="Search students, customers..."
                className="topbar-search-input"
              />
              {isSearchOpen && (
                <div className="topbar-search-dropdown">
                  <SearchStudents search={search} setSearch={setSearch} />
                </div>
              )}
            </div>
          </ClickAwayListener>
        </div>

        {/* Spacer */}
        <div className="topbar-spacer" />

        {/* Action Buttons */}
        <div className="topbar-actions">
          <Link to="/newcustomer" className="topbar-new-customer-btn">
            <Plus size={16} strokeWidth={2.5} />
            <span className="topbar-new-customer-text">New Customer</span>
          </Link>

          <button
            className="topbar-action-btn"
            onClick={() => setMessageDialogOpen(true)}
            aria-label="New Message"
            type="button"
          >
            <Mail size={18} />
          </button>

          <button
            className="topbar-action-btn topbar-lang-btn"
            onClick={changeLanguage}
            aria-label="Change Language"
            type="button"
          >
            <img
              src={language === 0 ? "/uk.png" : "/vn.png"}
              alt={language === 0 ? "English" : "Vietnamese"}
              className="topbar-flag"
            />
          </button>

          <ManualButton />

          <NotificationButton showNotifications={showNotifications} menuId={menuId} />

          {/* User Chip */}
          <div className="topbar-user-chip-container">
            <button
              className="topbar-user-chip"
              onClick={handleProfileMenuOpen}
              aria-label="Account of current user"
              aria-controls={menuId}
              aria-haspopup="true"
              aria-expanded={isMenuOpen}
              type="button"
            >
              <Avatar
                alt={user.name}
                src={`/images/${user.image}`}
                className="topbar-user-avatar"
              />
              <span className="topbar-user-name">{user.name}</span>
              <ChevronDown
                size={14}
                className={`topbar-user-chevron ${isMenuOpen ? "rotated" : ""}`}
              />
            </button>

            {/* User Menu */}
            <Menu
              open={isMenuOpen}
              onClose={() => setAnchorEl(null)}
              className="topbar-user-menu"
            >
              <MenuItem onClick={handleMenuClose}>
                <User size={16} className="topbar-menu-icon" />
                Profile
              </MenuItem>
              <MenuItem onClick={handleLogout}>
                <LogOut size={16} className="topbar-menu-icon" />
                Logout
              </MenuItem>
            </Menu>
          </div>
        </div>
      </nav>

      {/* Notifications */}
      <Notifications
        NanchorEl={NanchorEl}
        isNMenuOpen={isNMenuOpen}
        handleNotificationsClose={handleNotificationsClose}
      />

      {/* New Message Dialog */}
      {messageDialogOpen && (
        <NewMessageDialog
          messageDialogOpen={messageDialogOpen}
          setMessageDialogOpen={setMessageDialogOpen}
        />
      )}
    </div>
  );
}
